import { useMutation, useQuery } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";

const baseUrl = "/dm/V1/admin/user-information";

// ---------------------------------------------------------------------------
// Shape mirrors ShowUserInformationController.cs on the API side.
// ---------------------------------------------------------------------------
export interface SubjectGroup {
  subjectType: string;
  subjectValues: string[];
}

export interface UserInformationResponse {
  userId: string;
  domain?: string | null;
  fullName?: string | null;
  privileges: string[];
  subjects: SubjectGroup[];
}

export interface UserSearchHit {
  userId: string;
  fullName?: string | null;
  domain?: string | null;
}

export interface UserSearchResponse {
  items: UserSearchHit[];
  total: number;
  domain?: string;
  query?: string;
}

interface LookupArgs {
  userId: string;
  domain?: string;
}

/**
 * Look up one user's privilege + subject scope.
 * 404 -> caller can detect via the error path (axios surfaces the body).
 */
export const useUserInformationLookup = () =>
  useMutation({
    mutationFn: async ({
      userId,
      domain,
    }: LookupArgs): Promise<UserInformationResponse> => {
      const params: Record<string, string> = { userId };
      if (domain) params.domain = domain;
      const response = await apiClient.get<UserInformationResponse>(baseUrl, {
        params,
      });
      return response.data;
    },
  });

/**
 * Search the mock user roster by substring match. Returns up to 200 hits.
 */
export const useUserInformationSearch = (
  query: string,
  domain: string | undefined,
  enabled: boolean
) =>
  useQuery({
    queryKey: ["user-information-search", domain ?? "", query],
    enabled,
    queryFn: async (): Promise<UserSearchResponse> => {
      const response = await apiClient.get<UserSearchResponse>(
        `${baseUrl}/search`,
        { params: { domain, query } }
      );
      return response.data;
    },
  });
